import type { RulerLine } from "./models/RulerLine";
import type { RulersLayoutGrid } from "./models/RulersLayoutGrid";

/**
 * Snap candidate types.
 * Discriminated by the edge/center role for tooltip/highlight labelling.
 */
export type SnapCandidateType =
  | "element-top"
  | "element-bottom"
  | "element-center-y"
  | "element-left"
  | "element-right"
  | "element-center-x"
  | "viewport-edge"
  | "document-origin"
  | "grid-column-edge"
  | "grid-column-center"
  | "grid-row-edge"
  | "guide-gap";

export interface SnapCandidate {
  /** Document-coordinate position (px from document origin). */
  position: number;
  type: SnapCandidateType;
  /** Source DOM element, if this candidate came from an element edge/center. */
  el?: Element;
  /** Mirrored spacing between nearby guides. */
  gap?: number;
  /** Reference guides that define the mirrored spacing. */
  referenceLineIds?: [string, string];
  /** Custom display label for snap feedback. */
  label?: string;
}

export interface SnapResult {
  position: number;
  type: SnapCandidateType | "grid-step";
  el?: Element;
  gap?: number;
  referenceLineIds?: [string, string];
  label?: string;
}

export interface SnapEngineOptions {
  /**
   * Pixel distance within which a snap candidate is considered active.
   * Screen pixels — the same threshold applies regardless of scroll position.
   */
  threshold: number;

  /**
   * Grid step in document pixels. 0 / 1 = disabled.
   * Element/viewport/layout-grid candidates take priority when equidistant.
   */
  snapStep: number;

  /** Whether to collect element edge/center candidates. */
  snapToElements: boolean;

  /**
   * CSS selector used to collect element candidates.
   * All matching elements are measured via getBoundingClientRect().
   */
  snapSelector: string;

  /**
   * Layout grids (columns / rows) whose edges are also added as snap candidates.
   * Column edges are viewport-relative; row edges are document-relative.
   */
  layoutGrids?: RulersLayoutGrid[];
}

/**
 * Pure snap-calculation engine — no DOM mutations, no side-effects.
 *
 * Usage:
 *   1. Call `buildCandidates(axis, scrollX, scrollY)` once at drag start.
 *   2. Call `findSnap(rawDocPos)` on every pointermove.
 *   3. Use `snapToStep(pos)` as a lightweight grid-only fallback.
 */
export class SnapEngine {
  private opts: SnapEngineOptions;
  private candidates: SnapCandidate[] = [];

  constructor(opts: SnapEngineOptions) {
    this.opts = opts;
  }

  // ── Candidate collection ──────────────────────────────

  /**
   * Collects all snap candidates for the given drag axis.
   * Must be called at drag-start with the current scroll offsets.
   *
   * @param axis       "horizontal" → Y candidates; "vertical" → X candidates
   * @param scrollX    Current horizontal scroll (document px from origin)
   * @param scrollY    Current vertical scroll (document px from origin)
   */
  buildCandidates(
    axis: "horizontal" | "vertical",
    scrollX: number,
    scrollY: number,
    viewportWidth: number = window.innerWidth,
    viewportHeight: number = window.innerHeight,
    lines: RulerLine[] = [],
    excludeLineId?: string,
    extraCandidates?: SnapCandidate[],
  ): void {
    this.candidates = [];

    // ── Document origin ──
    this.candidates.push({ position: 0, type: "document-origin" });

    // ── Viewport edges (in document coords) ──
    if (axis === "horizontal") {
      this.candidates.push({ position: scrollY, type: "viewport-edge" });
      this.candidates.push({ position: scrollY + viewportHeight, type: "viewport-edge" });
    } else {
      this.candidates.push({ position: scrollX, type: "viewport-edge" });
      this.candidates.push({ position: scrollX + viewportWidth, type: "viewport-edge" });
    }

    // ── Element edges / centers ──
    if (this.opts.snapToElements && this.opts.snapSelector) {
      document.querySelectorAll(this.opts.snapSelector).forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;

        if (axis === "horizontal") {
          this.candidates.push({ position: rect.top + scrollY,    type: "element-top",      el });
          this.candidates.push({ position: rect.bottom + scrollY, type: "element-bottom",   el });
          this.candidates.push({
            position: (rect.top + rect.bottom) / 2 + scrollY,
            type: "element-center-y",
            el,
          });
        } else {
          this.candidates.push({ position: rect.left + scrollX,  type: "element-left",     el });
          this.candidates.push({ position: rect.right + scrollX, type: "element-right",    el });
          this.candidates.push({
            position: (rect.left + rect.right) / 2 + scrollX,
            type: "element-center-x",
            el,
          });
        }
      });
    }

    // ── Layout grid candidates ──
    this._addLayoutGridCandidates(axis, scrollX, scrollY, viewportWidth, viewportHeight);
    this._addGuideGapCandidates(axis, lines, excludeLineId);

    // ── External candidates (e.g. column layout) ──
    if (extraCandidates) {
      this.candidates.push(...extraCandidates);
    }
  }

  // ── Snap resolution ───────────────────────────────────

  /**
   * Returns the best snap result for a raw document position,
   * or `null` if nothing is within threshold.
   *
   * Element / viewport / layout-grid candidates beat grid-step when equidistant.
   */
  findSnap(rawDocPos: number): SnapResult | null {
    let bestDist = this.opts.threshold;
    let best: SnapCandidate | null = null;

    for (const c of this.candidates) {
      const dist = Math.abs(c.position - rawDocPos);
      if (dist < bestDist) {
        bestDist = dist;
        best = c;
      }
    }

    // Grid-step snap — only wins if strictly closer than any candidate
    if (this.opts.snapStep > 1) {
      const stepped = Math.round(rawDocPos / this.opts.snapStep) * this.opts.snapStep;
      const dist = Math.abs(stepped - rawDocPos);
      if (dist < bestDist) {
        return { position: stepped, type: "grid-step" };
      }
    }

    return best
      ? {
        position: best.position,
        type: best.type,
        el: best.el,
        gap: best.gap,
        referenceLineIds: best.referenceLineIds,
        label: best.label,
      }
      : null;
  }

  /**
   * Grid-step snap only — no threshold, no element candidates.
   * Used as a plain rounding fallback when no dynamic snap result is needed.
   */
  snapToStep(pos: number): number {
    if (this.opts.snapStep <= 1) return pos;
    return Math.round(pos / this.opts.snapStep) * this.opts.snapStep;
  }

  // ── Layout grid candidates ────────────────────────────

  private _addLayoutGridCandidates(
    axis: "horizontal" | "vertical",
    scrollX: number,
    scrollY: number,
    viewportWidth: number,
    viewportHeight: number,
  ): void {
    if (!this.opts.layoutGrids?.length) return;

    const vw = viewportWidth;
    const vh = viewportHeight;

    for (const grid of this.opts.layoutGrids) {
      if (grid.type === "columns" && axis === "vertical") {
        // Column grid → X candidates (converted to document coords via +scrollX)
        const { count, marginLeft = 0, marginRight = 0, gap = 0 } = grid;
        const usableWidth = vw - marginLeft - marginRight;
        if (usableWidth <= 0 || count <= 0) continue;
        const colWidth = (usableWidth - (count - 1) * gap) / count;

        // Outer margin edges
        this.candidates.push({ position: marginLeft + scrollX,         type: "grid-column-edge" });
        this.candidates.push({ position: vw - marginRight + scrollX,   type: "grid-column-edge" });

        for (let i = 0; i < count; i++) {
          const left   = marginLeft + i * (colWidth + gap);
          const right  = left + colWidth;
          const center = (left + right) / 2;
          // Inner column edges (exclude outer margins, already added above)
          if (i > 0)          this.candidates.push({ position: left   + scrollX, type: "grid-column-edge"   });
          if (i < count - 1)  this.candidates.push({ position: right  + scrollX, type: "grid-column-edge"   });
          this.candidates.push(                    { position: center + scrollX, type: "grid-column-center" });
        }
      }

      if (grid.type === "rows" && axis === "horizontal") {
        // Row grid → Y candidates in document coords (rows scroll with content)
        const { height, gap = 0 } = grid;
        const period = height + gap;
        if (period <= 0 || height <= 0) continue;

        // Cover the visible range + buffer so drags within the viewport always snap
        const rangeStart = Math.max(0, scrollY - 200);
        const rangeEnd   = scrollY + vh + 200;
        const firstRow   = Math.floor(rangeStart / period) * period;

        for (let pos = firstRow; pos < rangeEnd; pos += period) {
          this.candidates.push({ position: pos,          type: "grid-row-edge" });
          if (gap > 0) {
            // When there's a gap, the row bottom is also a distinct edge
            this.candidates.push({ position: pos + height, type: "grid-row-edge" });
          }
        }
      }
    }
  }

  private _addGuideGapCandidates(
    axis: "horizontal" | "vertical",
    lines: RulerLine[],
    excludeLineId?: string,
  ): void {
    const axisLines = lines
      .filter((line) => line.axis === axis && line.id !== excludeLineId)
      .slice()
      .sort((a, b) => a.position - b.position);

    if (axisLines.length < 2) return;

    for (let i = 0; i < axisLines.length; i++) {
      const base = axisLines[i];
      const prev = i > 0 ? axisLines[i - 1] : null;
      const next = i < axisLines.length - 1 ? axisLines[i + 1] : null;

      if (prev) {
        const gap = base.position - prev.position;
        if (gap > 0) {
          this.candidates.push({
            position: base.position + gap,
            type: "guide-gap",
            gap,
            referenceLineIds: [prev.id, base.id],
          });
        }
      }

      if (next) {
        const gap = next.position - base.position;
        if (gap > 0) {
          this.candidates.push({
            position: base.position - gap,
            type: "guide-gap",
            gap,
            referenceLineIds: [base.id, next.id],
          });
        }
      }
    }
  }
}
